import React, { useCallback } from "react";
import Markdown from "react-markdown";

import { useDispatch, useSelector } from "react-redux";
import {
  setDialogs,
  setLastDialog,
  setNewDialog,
  setSettings,
} from "../../store/slices/dialogSlice";
import Button from "../../ui/Button/Button";
import TextArea from "../../ui/TextArea/TextArea";
import { getAssistantList } from "../../utils/api/actions/assistant";
import {
  getAllDialogs,
  postMessage,
  unIgnoreDialog,
} from "../../utils/api/actions/dialogs";
import MessageContainer from "../MessageContainer/MessageContainer";
import VerticalFilterPanel from "../VerticalFilterPanel/VerticalFilterPanel";
import styles from "./DialogForm.module.scss";
import SettingsIcon from "../../assets/icons/SettingsIcon";

export default function DialogForm() {
  const dispatch = useDispatch();
  const elementRef = React.useRef(null);
  const chatRef = React.useRef(null);

  const newDialog = useSelector((state) => state.dialog.newDialog);
  const id =
    useSelector((state) => state.user?.data?.id) ||
    localStorage.getItem("@storage_User_Id");
  const dialogs = useSelector((state) => state.dialog.data);
  // const lastChatMessage = useSelector((state) => state.dialog.lastChatMessage);
  const settings = useSelector((state) => state.dialog.settings);
  const lastDialog = useSelector((state) => state.dialog.lastDialog);
  const activeDialog = useSelector((state) => state.dialog.activeDialog);

  const ignore = activeDialog?.settings?.ignore;

  const [systemMessage, setSystemMessage] = React.useState("");
  const [userMessage, setUserMessage] = React.useState("");
  const [isOpenVerticalFilterPanel, setIsOpenVerticalFilterPanel] =
    React.useState(false);

  // получение всех диалогов
  React.useEffect(() => {
    if (id) {
      dispatch(getAllDialogs(id));
      dispatch(getAssistantList(id));
    }
  }, [dispatch, id]);

  React.useEffect(() => {
    const dialog = dialogs?.find((el) => el.id === activeDialog?.id);
    if (dialog) {
      dispatch(setLastDialog(dialog));
    }
  }, [activeDialog, dialogs, dispatch]);

  React.useEffect(() => {
    if (activeDialog) {
      dispatch(setLastDialog(activeDialog));
    }
  }, [activeDialog, dispatch]);

  const handlerGetDialogId = useCallback(() => {
    dialogs?.forEach((el) => {
      if (el.id === activeDialog?.id) {
        dispatch(setSettings(el.settings));
      }
    });
  }, [dialogs, activeDialog, dispatch]);

  React.useEffect(() => {
    handlerGetDialogId();
  }, [dialogs, handlerGetDialogId]);

  React.useEffect(() => {
    if (settings) setSystemMessage(settings?.instruction);
  }, [settings]);

  React.useEffect(() => {
    if (newDialog) setSystemMessage(newDialog?.settings?.instruction);
  }, [newDialog]);

  const handlerSystemMessage = (e) => {
    setSystemMessage(e.target.value);

    if (newDialog) {
      let res = JSON.parse(JSON.stringify(newDialog));

      Object.defineProperty(res.settings, "instruction", {
        value: e.target.value,
      });

      dispatch(setNewDialog(res));
    }
    if (activeDialog) {
      let data = JSON.parse(JSON.stringify(activeDialog));

      Object.defineProperty(data.settings, "instruction", {
        value: e.target.value,
      });

      dispatch(setSettings(data));
    }
  };

  const handlerUserMessage = (e) => {
    setUserMessage(e.target.value);
  };

  const handlerSendMessage = () => {
    if (newDialog) {
      let res = JSON.parse(JSON.stringify(newDialog));
      res.content = userMessage;

      dispatch(postMessage(res));
      return setUserMessage("");
    }

    if (activeDialog) {
      let message = {
        content: userMessage,
        userId: id,
        settings: {
          instruction: systemMessage ? systemMessage : settings?.instruction, //'You are creative marketing expert', // инструкция, в дизайне окно Система (Введите системные инструкции)
          modelId: settings?.modelId, // модель/ассистент
          answerFormat: "JSON", // формат ответа (в дизайне нет его че-то, надо добавить)
          temperature: settings?.temperature, // Температура
          maxLength: settings?.maxLength, // Макс. длина
          topP: settings?.topP, // Top P
          frequencyPenalty: settings?.frequencyPenalty, // Штраф за частоту
          presencePenalty: settings?.presencePenalty, // Штраф за повторения
          assistantId: "", // айди ассистента – это потом далее развернем, пока не указывать
          dialogId: lastDialog ? lastDialog?.id : "", // айди диалога
        },
      };
      setUserMessage("");
      let res = JSON.parse(JSON.stringify(dialogs));
      res.forEach((dialog) => {
        if (dialog?.id === activeDialog?.id) {
          dialog?.histories.push({
            content: userMessage,
            role: "user",
            dialogId: dialog?.id,
          });
        }
      });
      dispatch(setDialogs(res));
      return dispatch(postMessage(message));
    }
  };

  const formatMessages = (role, message) => {
    return role === "user" ? message : <Markdown children={message} />;
  };

  const handlerKeySendPress = (event) => {
    if (event.metaKey && event.key === "Enter") {
      handlerSendMessage();
    }
  };

  const handleToggleVerticalPanel = () =>
    setIsOpenVerticalFilterPanel(!isOpenVerticalFilterPanel);

  const renderMessages = () => {
    if (lastDialog?.histories) {
      return lastDialog?.histories.map((message, index) => {
        return (
          <MessageContainer
            message={formatMessages(message?.role, message?.content)}
            role={message?.role}
            key={index}
          />
        );
      });
    }
  };

  // чат выбранного диалога
  const renderActiveMessages = () => {
    let res = [];

    dialogs.forEach((el) => {
      if (el.id === activeDialog?.id) {
        res = [...(el.histories || [])];
      }
    });

    return res
      .reverse()
      .map((message, index) => (
        <MessageContainer
          message={formatMessages(message.role, message.content)}
          role={message.role}
          key={index}
        />
      ));
  };

  const handlerUnIgnore = () => {
    if (ignore === true) {
      dispatch(unIgnoreDialog(activeDialog?.id));
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.chatContainer}>
        <TextArea
          label="система"
          placeholder="Введите системные инструкции"
          rows="20"
          value={systemMessage}
          onChange={handlerSystemMessage}
          readOnly={true}
        />
        <div className={styles.chatPanel} ref={chatRef}>
          {dialogs && !newDialog && !activeDialog && renderMessages()}
          {activeDialog && !newDialog && renderActiveMessages()}
          <div ref={elementRef} />
        </div>
        {(dialogs?.length > 0 || newDialog) && (
          <div className={styles.userContainer}>
            {ignore && (
              <div className={styles.unIgnoreContainer}>
                <Button
                  className={styles.unIgnoreButton}
                  titleClassName={styles.unIgnoreButtonTitle}
                  title={"Передать ассистенту"}
                  onClick={handlerUnIgnore}
                />
              </div>
            )}
            <div className={styles.userInputContainer}>
              <Button
                title="Отправить"
                className={styles.userButton}
                titleClassName={styles.titleClassName}
                onClick={handlerSendMessage}
              />

              <TextArea
                placeholder="Введите запрос"
                classNameTextArea={styles.userInput}
                rows="5"
                value={userMessage}
                onChange={handlerUserMessage}
                onKeyDown={handlerKeySendPress}
                onEnterPress={handlerSendMessage}
              />
            </div>
          </div>
        )}
      </div>

      {isOpenVerticalFilterPanel && (
        <VerticalFilterPanel
          settings={settings}
          activeDialog={activeDialog}
          dialogs={dialogs}
        />
      )}
      <div
        className={styles.settings}
        style={{ marginLeft: isOpenVerticalFilterPanel ? "15px" : "0px" }}
        onClick={handleToggleVerticalPanel}
      >
        <SettingsIcon size={40} />
      </div>
    </div>
  );
}
